// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Alert } from '@mui/material';
import { type TargetPortfolioReasonCode } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { Translation } from '@shared/ui-kit';
import { useTraderPortfolioData } from '../hooks/use-trader-portfolio-data';

const REASON_I18N_KEYS = {
  insufficient_quantity: 'trader-simulation.insufficient_quantity',
  insufficient_cash: 'trader-simulation.insufficient_cash',
  missing_position: 'trader-simulation.missing_position',
  missing_cash_position: 'trader-simulation.missing_cash_position',
  unvalued_position: 'trader-simulation.unvalued_position',
} as const satisfies Record<TargetPortfolioReasonCode, string>;

const alertClassName =
  'mb-4 w-fit max-w-[30%] !border-0 !bg-transparent !p-0 [&_.MuiAlert-message]:!p-0';

export const TraderSimulationAlerts = () => {
  const { simulationStatus, operationResults, unsharedProposalAccounts } =
    useTraderPortfolioData();

  const failedReasons = Array.from(
    new Set(
      operationResults
        .filter(
          result =>
            result.status !== 'applied' &&
            result.reason_code !== null &&
            result.reason_code !== undefined &&
            result.reason_code in REASON_I18N_KEYS,
        )
        .map(result => result.reason_code as TargetPortfolioReasonCode),
    ),
  );

  const showStatusFallback =
    failedReasons.length === 0 &&
    (simulationStatus === 'partial' || simulationStatus === 'failed');

  if (
    failedReasons.length === 0 &&
    !showStatusFallback &&
    unsharedProposalAccounts.length === 0
  ) {
    return null;
  }

  return (
    <div data-testid="trader-simulation-alerts">
      {unsharedProposalAccounts.map(account => (
        <Alert
          key={`${account.institutionName}-${account.iban}`}
          severity="error"
          icon={false}
          className={alertClassName}
          data-testid="trader-unshared-account-alert"
        >
          <p className="text-base text-negative">
            <Translation
              i18nKey="trader-simulation.unshared-account"
              ns="components"
              values={{
                name: account.name,
                institutionName: account.institutionName,
              }}
            />
          </p>
        </Alert>
      ))}

      {failedReasons.map(reasonCode => (
        <Alert
          key={reasonCode}
          severity="error"
          icon={false}
          className={alertClassName}
          data-testid="trader-simulation-status-alert"
        >
          <p className="text-base text-negative">
            <Translation i18nKey={REASON_I18N_KEYS[reasonCode]} ns="components" />
          </p>
        </Alert>
      ))}

      {showStatusFallback && (
        <Alert
          severity="error"
          icon={false}
          className={alertClassName}
          data-testid="trader-simulation-status-alert"
        >
          <p className="text-base text-negative">
            <Translation
              i18nKey={
                simulationStatus === 'failed'
                  ? 'trader-simulation.failed-title'
                  : 'trader-simulation.partial-title'
              }
              ns="components"
            />
          </p>
        </Alert>
      )}
    </div>
  );
};
